import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// In-memory cache so we don't re-fetch the same photo when scrolling back up
// or when the same item appears in multiple lists (Dashboard + Items page).
const photoCache = new Map(); // itemId -> photo string (data URI or path)
const inflight = new Map();   // itemId -> Promise resolving to photo string

async function fetchItemPhoto(itemId) {
  if (photoCache.has(itemId)) return photoCache.get(itemId);
  if (inflight.has(itemId)) return inflight.get(itemId);
  const p = api.get(`/items/${itemId}/photo`).then(({ data }) => {
    const v = data?.photo || "";
    photoCache.set(itemId, v);
    inflight.delete(itemId);
    return v;
  }).catch(() => { inflight.delete(itemId); return ""; });
  inflight.set(itemId, p);
  return p;
}

// Lazy-loading item thumbnail. Pass `itemId` + `hasPhoto`; we only fetch when
// the placeholder scrolls into view (IntersectionObserver). Falls back to a
// soft sage placeholder when there's no photo.
export default function LazyItemPhoto({ itemId, hasPhoto, alt = "", className = "" }) {
  const [photo, setPhoto] = useState(() => photoCache.get(itemId) || "");
  const ref = useRef(null);

  useEffect(() => {
    if (!hasPhoto || !itemId) return;
    if (photoCache.has(itemId)) {
      setPhoto(photoCache.get(itemId));
      return;
    }
    if (!ref.current) return;
    const el = ref.current;
    // If IntersectionObserver is unavailable (SSR / old browsers), fetch immediately
    if (typeof IntersectionObserver === "undefined") {
      fetchItemPhoto(itemId).then((v) => { if (v) setPhoto(v); });
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            fetchItemPhoto(itemId).then((v) => { if (v) setPhoto(v); });
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" } // start loading just before visible
    );
    io.observe(el);
    return () => io.disconnect();
  }, [itemId, hasPhoto]);

  if (!hasPhoto) {
    return (
      <div
        ref={ref}
        className={`bg-gradient-to-br from-sage-400/20 via-sand-200 to-terracotta-50 ${className}`}
        data-testid="lazy-photo-empty"
      />
    );
  }

  if (!photo) {
    return (
      <div
        ref={ref}
        className={`bg-sand-200 animate-pulse ${className}`}
        data-testid="lazy-photo-loading"
      />
    );
  }

  // Photo is either a data URI, absolute URL, or storage path
  const isDirect = /^data:/i.test(photo) || /^https?:\/\//i.test(photo);
  const src = isDirect ? photo : `/api/files/${photo}`;
  return <img ref={ref} src={src} alt={alt} className={className} data-testid="lazy-photo-loaded" loading="lazy" />;
}
